import { gql, type TypedDocumentNode } from "@apollo/client";
import type { CommissionProgramConsentNode } from "@/pages/billing/finance/commission/consents/types";
import type { CommissionEnrollmentAccountStatus } from "@/pages/billing/finance/commission/enrollments/types";
import type { CommissionLineNode } from "@/pages/billing/finance/commission/lines/types";
import type { PersonGrpAllocationAgreementNode, PersonGrpPaidOutTotal } from "./types";
import type { GrpEnrollmentStatus } from "@/pages/billing/finance/grp/enrollments/types";
import type { PersonGrpEnrollmentNode } from "./_components/matchPersonGrpEnrollment";

export interface PersonGrpContextQueryResponse {
  person: {
    id: string;
    displayName: string;
    pgaId: string;
  };
}

export const PERSON_GRP_CONTEXT_QUERY: TypedDocumentNode<PersonGrpContextQueryResponse, { pgaId: string }> = gql`
  query PersonGrpContext($pgaId: ID!) {
    person(pgaId: $pgaId) {
      id
      displayName
      pgaId
    }
  }
`;

export interface PersonGrpFaaCampaignIdsQueryResponse {
  person: {
    id: string;
    commissionBenefitProgram: {
      programConsents: {
        nodes: { id: string; commissionCampaignId: string }[];
      };
    } | null;
  };
}

export const PERSON_GRP_FAA_CAMPAIGN_IDS_QUERY: TypedDocumentNode<
  PersonGrpFaaCampaignIdsQueryResponse,
  { pgaId: string }
> = gql`
  query PersonGrpFaaCampaignIds($pgaId: ID!) {
    person(pgaId: $pgaId) {
      id
      commissionBenefitProgram(programSlug: "grp") {
        programConsents {
          nodes {
            id
            commissionCampaignId
          }
        }
      }
    }
  }
`;

export interface PersonGrpBenefitsQueryResponse {
  person: {
    id: string;
    displayName: string;
    pgaId: string;
    commissionBenefitProgram: {
      program: {
        id: string;
        name: string;
        slug: string;
      };
      enrollment: {
        id: string;
        accountStatus: CommissionEnrollmentAccountStatus;
        program: {
          id: string;
          name: string;
          slug: string;
        };
      } | null;
      programConsents: {
        nodes: CommissionProgramConsentNode[];
        totalCount: number;
      };
      allocationAgreements: {
        nodes: PersonGrpAllocationAgreementNode[];
        totalCount: number;
      };
      receivedAllocationAgreements: {
        nodes: PersonGrpAllocationAgreementNode[];
        totalCount: number;
      };
      lines: {
        nodes: CommissionLineNode[];
        totalCount: number;
        pageInfo: {
          hasNextPage: boolean;
          hasPreviousPage: boolean;
          startCursor?: string;
          endCursor?: string;
        };
      };
      paidOutTotal: PersonGrpPaidOutTotal;
    };
  };
}

export interface PersonGrpBenefitsQueryVariables {
  pgaId: string;
  paidOutFrom: string;
  paidOutTo: string;
}

const PERSON_GRP_ALLOCATION_AGREEMENT_FIELDS = gql`
  fragment PersonGrpAllocationAgreementFields on BackofficeCommissionAllocationAgreementConnection {
    nodes {
      id
      commissionCampaignId
      receiverCrmPersonId
      crmFacilityId
      facilityName
      status
      effectiveStart
      effectiveEnd
      campaign {
        id
        editionYear
        status
        partner {
          id
          name
        }
      }
      splits {
        id
        crmPersonId
        pgaId
        displayName
        splitPercent
        hasActiveFaa
      }
    }
    totalCount
  }
`;

export const PERSON_GRP_BENEFITS_QUERY: TypedDocumentNode<
  PersonGrpBenefitsQueryResponse,
  PersonGrpBenefitsQueryVariables
> = gql`
  query PersonGrpBenefits($pgaId: ID!, $paidOutFrom: ISO8601Date!, $paidOutTo: ISO8601Date!) {
    person(pgaId: $pgaId) {
      id
      displayName
      pgaId
      commissionBenefitProgram(programSlug: "grp") {
        program {
          id
          name
          slug
        }
        enrollment {
          id
          accountStatus
          program {
            id
            name
            slug
          }
        }
        programConsents {
          nodes {
            id
            commissionCampaignId
            crmPersonId
            attributionModel
            ownership
            startsAt
            endsAt
            status
            person {
              id
              displayName
              pgaId
            }
            campaign {
              id
              editionYear
              partner {
                id
                name
              }
              program {
                id
                name
              }
            }
          }
          totalCount
        }
        allocationAgreements {
          ...PersonGrpAllocationAgreementFields
        }
        receivedAllocationAgreements {
          ...PersonGrpAllocationAgreementFields
        }
        lines(first: 10) {
          nodes {
            id
            person {
              id
              displayName
              pgaId
            }
            amountCents
            fundingStatus
            payoutEligibilityStatus
            holdReasons
            lineKind
            receivedAt
            payableAt
            paidOutAt
            expiredAt
            facilityName
            fundingBatch {
              id
              status
            }
            releaseBatch {
              id
              status
            }
            splitLines {
              id
            }
            campaign {
              id
              editionYear
              partner {
                id
                name
              }
              program {
                id
                name
              }
            }
          }
          totalCount
          pageInfo {
            hasNextPage
            hasPreviousPage
            startCursor
            endCursor
          }
        }
        paidOutTotal(from: $paidOutFrom, to: $paidOutTo) {
          amountCents
          lineCount
          from
          to
        }
      }
    }
  }
  ${PERSON_GRP_ALLOCATION_AGREEMENT_FIELDS}
`;

export interface PersonGrpEnrollmentQueryResponse {
  grpEnrollments: {
    nodes: PersonGrpEnrollmentNode[];
    pageInfo: {
      hasNextPage: boolean;
      endCursor?: string | null;
    };
  };
}

export interface PersonGrpEnrollmentQueryVariables {
  first?: number;
  after?: string;
  filters?: {
    search?: string;
    status?: GrpEnrollmentStatus;
  };
}

export const PERSON_GRP_ENROLLMENT_QUERY: TypedDocumentNode<
  PersonGrpEnrollmentQueryResponse,
  PersonGrpEnrollmentQueryVariables
> = gql`
  query PersonGrpEnrollment($first: Int, $after: String, $filters: BackofficeGrpEnrollmentsInput) {
    grpEnrollments(first: $first, after: $after, filters: $filters) {
      nodes {
        id
        accountOpenedOn
        person {
          pgaId
        }
      }
      pageInfo {
        hasNextPage
        endCursor
      }
    }
  }
`;
